import { Avatar, Box, Grow, Typography } from "@mui/material";
import React from "react";
import { useThemeContext } from "../../globalContext/global-context";

import type { Message, MessageContent } from "../../session/messages";

export function MuiMessage({
  id,
  message,
  showDate,
  showTime,
}: Readonly<{
  id: string;
  message: Message<MessageContent>;
  showDate: boolean;
  showTime: boolean;
}>): React.ReactElement {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  if (message.deletedAt) {
    return <div id={id} />;
  }

  const dispDate = message.updatedAt ?? message.createdAt;

  const ChatAvator = (
    <Box
      sx={{
        minWidth: 0,
        flexShrink: 0,
        marginLeft: message.self ? `${theme.tokens.spacing["2"]}px` : 0,
        marginRight: message.self ? 0 : `${theme.tokens.spacing["2"]}px`,
      }}
    >
      <Avatar alt={message.username} src={message.avatar} />
    </Box>
  );

  const ChatUsername = (
    <Box
      sx={{ maxWidth: "100%", marginInline: `${theme.tokens.spacing["2"]}px` }}
    >
      <Typography variant="body2" align={message.self ? "right" : "left"}>
        {message.username}
      </Typography>
    </Box>
  );

  const ChatDate = (
    <Box
      sx={{ maxWidth: "100%", marginInline: `${theme.tokens.spacing["2"]}px` }}
    >
      <Typography
        variant="body2"
        align={message.self ? "right" : "left"}
        color="textSecondary"
      >
        {dispDate?.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </Typography>
    </Box>
  );

  return (
    <Grow in>
      <Box sx={{ maxWidth: "100%", display: "flex", flexDirection: "column" }}>
        {showDate && (
          <Typography align="center">
            {dispDate?.toLocaleDateString()}
          </Typography>
        )}
        <Box
          id={id}
          sx={{
            maxWidth: "100%",
            marginBlock: `${theme.tokens.spacing["2"]}px`,
            pl: message.self ? "20%" : 0,
            pr: message.self ? 0 : "20%",
            display: "flex",
            justifyContent: message.self ? "flex-end" : "flex-start",
          }}
          style={{ overflowWrap: "break-word" }}
        >
          {/* c8 ignore start */}
          {message.avatar && !message.self && ChatAvator}
          {/* c8 ignore end */}
          <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
            {message.username && ChatUsername}
            <Box
              sx={{
                maxWidth: "100%",
                paddingBlock: `${theme.tokens.spacing["2"]}px`,
                paddingInline: `${theme.tokens.spacing["4"]}px`,
                bgcolor: message.self ? "primary.main" : "background.paper",
                color: message.self ? "primary.contrastText" : "text.primary",
                borderRadius: `${theme.tokens.shape.radius.lg}px`,
                boxShadow: theme.tokens.elevation["2"],
              }}
            >
              {message.type === "text" && (
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: theme.tokens.typography.size.md,
                    lineHeight: theme.tokens.typography.lineHeight.normal,
                  }}
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {message.content}
                </Typography>
              )}
              {message.type === "jsx" && <div>{message.content}</div>}
            </Box>
            {showTime && ChatDate}
          </Box>
          {message.avatar && message.self && ChatAvator}
        </Box>
      </Box>
    </Grow>
  );
}
